import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { applyThemeAttribute, getStoredTheme, resolveTheme, storeTheme, safeLocalStorage } from '../utils/theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';
const ThemeContext = createContext(null);

function systemQuery() {
  return typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
}

export function ThemeProvider({ children }) {
  const [stored, setStored] = useState(() => getStoredTheme(safeLocalStorage()));
  const [systemDark, setSystemDark] = useState(() => Boolean(systemQuery()?.matches));

  // Follow live OS changes; only matters while nothing is stored.
  useEffect(() => {
    const query = systemQuery();
    if (!query) return undefined;
    const onChange = (event) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const theme = resolveTheme(stored, systemDark);

  useEffect(() => {
    applyThemeAttribute(document.documentElement, theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    storeTheme(safeLocalStorage(), next);
    setStored(next);
  }, [theme]);

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
}
